// eslint-disable-next-line no-unused-vars
import React, { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { string, func, bool, object } from 'prop-types';
import styled from '@emotion/styled';
import {
  InlineLoading,
  Loading,
  // Checkbox,
  Search,
  ComposedModal,
  ModalHeader,
  ModalBody
} from '@carbon/react';
import uniqueId from 'lodash/uniqueId';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import { Package } from 'lucide-react';

import Img from 'Containers/Image/Image';
import AddToCart from 'Components/AddToCart/AddToCart';
import Price from 'Components/Price/Price';
import { renderStock } from 'Components/Products/helpers';
import { isProductUnavailableForOffer } from 'Utils/offers';

// const StyledOrderProductsModalComponent = styled(ComposedModal)`
// `;

const StyledSearch = styled(Search)`
  position: relative;
  border-radius: 6px;
  width: 100%;
  margin-bottom: 15px;

  .cds--search-input {
    &:focus,
    &:active {
      outline: 0 none;
    }

    height: 50px;
    border-radius: 6px;
    background-color: ${ props => props.theme.colors.white };
    padding-left: 15px;
    border: 1px solid ${ props => props.theme.colors.gray };
  }

  .cds--search-magnifier {
    display: none;
  }

  .cds--search-close {
    margin-right: 20px;
    
    &:focus,
    &:hover,
    &:active {
      outline: 0 none;
      background-color: transparent;
      border: 0;
      box-shadow: none;

      svg {
        fill: ${ props => props.theme.colors.darkGray };
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    padding: 0 20px;
  }
`;

const StyledResultsContainer = styled.div`
  width: 100%;
  font-size: 0.875rem;
  background-color: ${ props => props.theme.colors.white };
  color: ${ props => props.theme.colors.darkGray };

  .cds--loading {
    max-width: 2rem;
    max-height: 2rem;
  }

  .products {
    max-height: 375px;
    overflow: auto;
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    .details {
      title: 
    }

    .products {
      max-height: none;
    }
  }
`;

const StyledLink = styled(Link)`
  margin: 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.25em;
  color: inherit;

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    -webkit-line-clamp: 4;
  }
`;

// const StyledRow = styled.div`
//   font-size: 0.875rem;
//   height: 40px;
//   padding: 0 1rem;
//   display: flex;
//   align-items: center;
//   justify-content: space-between;
//   background-color: ${ props => props.theme.colors.shadowGray };
//   color: ${ props => props.theme.colors.darkGray };
//
//   > p {
//     font-weight: 600;
//   }
//
//   .cds--checkbox-wrapper {
//     cursor: pointer;
//     flex-grow: 0;
//
//     label {
//       &:before {
//         width: 14px;
//         height: 14px;
//         background-color: ${ props => props.theme.colors.white };
//       }
//
//       &:after {
//         top: 7px;
//         left: 6px;
//       }
//
//       color: inherit;
//     }
//   }
// `;

const StyledModalBody = styled(ModalBody)`
  background-color: ${ props => props.theme.colors.white };
  margin: 0;
  padding-bottom: 50px;

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    padding: 0;
  }
`;

const StyledProductSearchItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  height: 125px;
  box-sizing: border-box;
  max-height: 125px;
  border-bottom: 1px solid ${ props => props.theme.colors.shadowGray };

  .container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  
    .preview {
      min-width: 70px;
      max-width: 70px;
      height: 70px;
      padding: 6px;
      margin-right: 1rem;
      border: 1px solid ${ props => props.theme.colors.shadowGray };
      box-sizing: border-box;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      align-self: center;
  
      a {
        display: block;
        width: 100%;
  
        svg {
          position: relative;
          display: block;
          margin: 0 auto;
          height: auto;
          width: auto;
          max-height: 100%;
          max-width: 100%;
        }
  
        > * {
          margin: 0 auto;
        }
      }
  
      img {
        display: block;
        height: auto;
        width: 100%;
      }
    }
  
    .right {
      flex-grow: 1;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      max-width: calc(100% - 70px - 1rem);
    }
  
    .details {
      flex-grow: 1;
      padding-right: 1rem;
      font-size: 0.875rem;
      color: ${ props => props.theme.colors.darkGray };
      overflow: hidden;
      max-width: calc(70% - 1rem);
  
      .title {
        font-size: inherit;
        line-height: 1.35rem;
        margin: 0;
  
        a {
          color: inherit;
        }
      }
  
      .code, .stock {
        font-weight: 600;
        font-size: inherit;
  
        span {
          margin: 0 5px;
          font-weight: normal;
        }
      }
  
      .stock {
        &.inStock {
          span {
            color: ${ props => props.theme.products.colors.inStock };
          }
        }
  
        &.outOfStock {
          span {
            color: ${ props => props.theme.colors.default };
          }
        }
  
        &.onDemand {
          span {
            color: ${ props => props.theme.colors.darkGray };
          }
        }
  
        span {
          text-transform: lowercase;
        }
      }
    }
  
    .priceContainer {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      min-width: 25%;
  
      .price {
        margin-bottom: 5px;
  
        .oldPrice {
          color: ${ props => props.theme.colors.gray };
          font-size: 0.875rem;
        }
  
        .currentPrice {
          color: ${ props => props.theme.colors.default };
          font-size: 1rem;
        }
      }
  
      .stockAlert {
        padding: 0 10px;
        min-height: 0;
        height: 30px;
        font-size: 0.875rem;
        border: 1px solid ${ props => props.theme.colors.default };
        color: ${ props => props.theme.colors.default };
        fill: ${ props => props.theme.colors.default };
        background-color: transparent;
      }
  
      .addToCart {
        padding: 0 10px;
        height: 30px;
        font-size: 0.875rem;
      }
    }
  }

  .priceContainerMobile {
    display: none;
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    height: auto;
    max-height: none;
    padding: 20px;
    border-color: #e2e2e2;

    .container {
      align-items: flex-start;
  
      .preview {
        align-self: auto;
      }
  
      .details {
        max-width: none;
  
        > p, > div {
          display: none;
        }
      }
  
      .priceContainer {
        display: none;
      }
    }

    .priceContainerMobile {
      display: flex;
      flex-direction: column;
      gap: 15px;

      .productDetails {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
      }

      .price {
        flex-grow: unset;
        font-size: 14px;
      }

      .addToCart {
        min-width: 100%;
      }

      .code, .stock {
        font-weight: 600;
        font-size: inherit;
  
        span {
          margin: 0 5px;
          font-weight: normal;
        }
      }
  
      .stock {
        &.inStock {
          span {
            color: ${ props => props.theme.products.colors.inStock };
          }
        }
  
        &.outOfStock {
          span {
            color: ${ props => props.theme.colors.default };
          }
        }
  
        &.onDemand {
          span {
            color: ${ props => props.theme.colors.darkGray };
          }
        }
  
        span {
          text-transform: lowercase;
        }
      }
    }
  }
`;

const StyledInlineLoading = styled(InlineLoading)`
  padding: 0 1rem;
  min-height: 0;
  margin: ${ props => props.status === 'active' || props.status === 'error' ? '0.875rem 0' : 0 };
`;

const OrderProductsModalComponent = memo(props => {
  const {
    searchRef,
    currency,
    open,
    searchResults,
    onClose,
    search,
    isLoading,
    error,
    // onlyAvailable,
    onAddToOrder,
    // onToggleAvailable,
    onKeyDown,
    isValid,
    onChange,
    isExternalReseller,
    title,
    addButtonLabel,
    trackingLocation,
    buttonAnalyticsEvent,
    requiresAddToCartPermission,
    allowOutOfStock
  } = props;

  const { t } = useTranslation();
  const theme = useTheme();

  const renderProducts = products => products.map(product => (
    <StyledProductSearchItem key={ product.id } theme={ theme }>
      <div className="container">
        <div className="preview">
          <Link to={ { pathname: `/products/${ product.slug }/pd/${ product.id }` } }>
            <Img
              src={ product?.pictures[0]?.urlOverlayPicture }
              unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
              loader={ <Loading active withOverlay={ false } small /> }
              alt={ product.title }
            />
          </Link>
        </div>
        <div className="right">
          <div className="details">
            <StyledLink
              className="title"
              to={ { pathname: `/products/${ product.slug }/pd/${ product.id }` } }
            >
              { product.title }
            </StyledLink>
            <p className="code">{ t('product.code') }:<span>{ product.code }</span></p>
            { !product?.warnings?.blocked_stocks && renderStock({ t, product }) }
          </div>
          <div className="priceContainer">
            { !product?.warnings?.blocked_prices && (
              <Price
                price={ isExternalReseller ? product?.price : product.ronPrice }
                promoPrice={ isExternalReseller ? product?.promoPrice : product?.ronPromoPrice }
                currency={ currency || product.currency }
              />
            )}
            <AddToCart
              onClick={ () => onAddToOrder(product) }
              trackingContext={ {
                location: trackingLocation,
                product_sku: product?.code,
              } }
              analyticsEvent={ buttonAnalyticsEvent }
              requiresAddToCartPermission={ requiresAddToCartPermission }
              label={ addButtonLabel || t('order.addToReservation') }
              disabled={
                (!allowOutOfStock
                  && Number(product.stockValue || 0) < Math.max(1, Number(product.minQuantity || 1)))
                || isProductUnavailableForOffer(product)
              }
              showSelector={ false }
              id={ product.id }
              size="md"
            />
          </div>
        </div>
      </div>
      <div className="priceContainerMobile">
        <div className="productDetails">
          <div className="productInfo">
            <p className="code">{ t('product.code') }:<span>{ product.code }</span></p>
            { !product?.warnings?.blocked_stocks && renderStock({ t, product }) }
          </div>
          { !product?.warnings?.blocked_prices && (
            <Price
              price={ isExternalReseller ? product?.price : product.ronPrice }
              promoPrice={ isExternalReseller ? product?.promoPrice : product?.ronPromoPrice }
              currency={ currency || product.currency }
            />
          )}
        </div>
        <AddToCart
          onClick={ () => onAddToOrder(product) }
          trackingContext={ {
            location: `${ trackingLocation }_mobile`,
            product_sku: product?.code,
          } }
          analyticsEvent={ buttonAnalyticsEvent }
          requiresAddToCartPermission={ requiresAddToCartPermission }
          label={ addButtonLabel || t('order.addToReservation') }
          disabled={
            (!allowOutOfStock
              && Number(product.stockValue || 0) < Math.max(1, Number(product.minQuantity || 1)))
            || isProductUnavailableForOffer(product)
          }
          showSelector={ false }
          id={ product.id }
          size="md"
        />
      </div>
    </StyledProductSearchItem>
  ));

  const memoizedProducts = useMemo(
    () => {
      if (searchResults?.products?.length) {
        return renderProducts(searchResults.products);
      }
      return null;
    },
    [allowOutOfStock, onAddToOrder, searchResults?.products]
  );
  // eslint-disable-next-line no-nested-ternary
  const status = isLoading
    ? 'active'
    : error
      ? 'error'
      : null;

  return (
    <ComposedModal
      theme={ theme }
      open={ open }
      size="md"
      onClose={ onClose }
      className="OrderProductsModal"
      selectorPrimaryFocus=".cds--modal-content"
    >
      <ModalHeader>
        <h4>{ title || t('order.addProducts') }</h4>
      </ModalHeader>
      <StyledModalBody theme={ theme } hasForm>
        <StyledSearch
          ref={ searchRef }
          autoFocus
          theme={ theme }
          id={ uniqueId('search_order_products') }
          labelText=""
          light
          value={ search }
          placeholder={ t('common.search.placeholder') }
          onKeyDown={ onKeyDown }
          onChange={ onChange }
        />
        <StyledResultsContainer theme={ theme }>
          {/*<StyledRow theme={ theme }>*/}
          {/*  <p>{ t('common.search.placeholder') }</p>*/}
          {/*  <Checkbox*/}
          {/*    id="onlyAvailableProducts"*/}
          {/*    labelText={ t('common.search.onlyAvailable') }*/}
          {/*    onChange={ onToggleAvailable }*/}
          {/*    checked={ onlyAvailable }*/}
          {/*  />*/}
          {/*</StyledRow>*/}
          <StyledInlineLoading
            status={ status }
            description={ isLoading ? t('common.loading_products') : error || '' }
          />
          { !isValid && <p className="error">{ t('common.search.minSearchLength') }</p> }
          <div className="products">
            { memoizedProducts?.length > 0 && memoizedProducts }
            { !isLoading && !memoizedProducts && t('products.noProductsAvailable') }
          </div>
        </StyledResultsContainer>
      </StyledModalBody>
    </ComposedModal>
  );
});

OrderProductsModalComponent.propTypes = {
  currency: string,
  onKeyDown: func,
  onChange: func,
  onClose: func,
  // onToggleAvailable: func,
  onAddToOrder: func,
  error: string,
  search: string,
  isValid: bool,
  searchResults: object,
  isLoading: bool,
  // onlyAvailable: bool,
  open: bool,
  searchRef: object,
  isExternalReseller: bool,
  title: string,
  addButtonLabel: string,
  trackingLocation: string,
  buttonAnalyticsEvent: string,
  requiresAddToCartPermission: bool,
  allowOutOfStock: bool
};

OrderProductsModalComponent.defaultProps = {
  trackingLocation: 'order_products_modal',
  buttonAnalyticsEvent: 'add_to_cart',
  requiresAddToCartPermission: true,
  allowOutOfStock: false
};
OrderProductsModalComponent.displayName = 'OrderProductsModalComponent';

export default OrderProductsModalComponent;
